import { useMemo } from 'react';
import { useAttractionsContext } from '@/common/app/contexts';
import { Preloader } from '@/common/ui/loaders/Preloader';
import TopAttractions from '@/shared/TopAttractions/ui/TopAttractions';
import { ActivitiesList } from '@/screens/AttractionsPage/ui/ActivitiesList';
import { Layout } from '@/shared/Layout/ui';
import { TPropsLandmarksDestinationPage } from '../domain/LandmarksDestination.domain';
import * as S from './LandmarkDestinationPage.style';
import { Container } from '@/common/ui/containers/Container';
import { CardsContainer } from '@/screens/HomePage/ui/CardsContainer';
import { BlogCard } from '@/entities/Blogs/ui/BlogCard';
import URL from '@/common/app/utils/urlsUtils';
import { useMedia } from '@/common/service/hooks/useMedia';
import { CommonMeta } from '@/common/ui/meta/CommonMeta';
import { createLandmarkScheme } from '../service/createLandmarkScheme';
import { NewFeaturedActivities } from '@/shared/FeaturedActivities/ui/NewFeaturedActivities';
import { LandmarkContent } from './LandmarkContent';
import Paragraph from '@/shared/Paragraph/ui/Paragraph';
import { AnswersFAQ } from '@/screens/ActivityPage/ui/Answers/AnswersFAQ';

export const LandmarkPage = ({
    currentDestination,
    title,
    subTitle,
    landmark,
    fullList,
    featuredCategories,
    heroImages,
    blogs,
    images,
    breadcrumbs,
    meta,
    placeholders,
    recommendedTrips,
}: TPropsLandmarksDestinationPage) => {
    const { settings, setSettings, loading, setLoading } = useAttractionsContext();
    const isDesktop = useMedia('(min-width: 992px)');
    const { name, id, slug, state } = currentDestination;
    const item_list_id = id;
    const item_list_name = name;
    const ogTitle = `${landmark.name} in ${name}, ${
        state.code
    } [${new Date().getFullYear()} Visitors Guide]`;

    const scheme = useMemo(
        () =>
            createLandmarkScheme({
                path: `/destination/${state.code}/${name}/landmarks/${landmark.slug}/`,
                name: landmark.name,
                description: landmark.data.description,
                image: heroImages[0],
                latitude: landmark.latitude,
                longitude: landmark.longitude,
                destinationName: name,
                destinationCode: state.code,
                touristType: landmark.data.touristType,
                publicAccess: landmark.data.publicAccess,
                isAccessibleForFree: landmark.data.isAccessibleForFree,
            }),
        [name, state, landmark, heroImages]
    );

    return (
        <Layout
            TitleName={title}
            SubTitle={subTitle}
            images={heroImages}
            breadcrumbs={breadcrumbs}
            withMobileHeroImage
            noSearch
        >
            <CommonMeta
                title={meta.title}
                description={meta.description}
                slug=""
                ogUrl={meta.canonical}
                ogType="article"
                ogTitle={ogTitle}
                ogImageAlt={ogTitle}
                imageSrc={heroImages[0]}
                canonical={meta.canonical}
                structuredData={[scheme]}
                lcpImagePreload={heroImages[0]}
            />
            <Container>
                <S.ShortParagraph data-test-id="Description">
                    <Paragraph
                        className="table-layout"
                        id="ts_paragraph"
                        as="div"
                        innerHtml={landmark.data.description}
                    />
                </S.ShortParagraph>
            </Container>

            <NewFeaturedActivities
                defaultActivities={recommendedTrips || []}
                title={`Featured Activities Near ${landmark.name}`}
                item_list_id={item_list_id}
                item_list_name={item_list_name}
                placeholders={placeholders}
            />
            <LandmarkContent landmark={landmark} images={images} />

            {!!featuredCategories?.length && (
                <TopAttractions
                    featuredCategories={featuredCategories ?? []}
                    destinationName={currentDestination.name}
                    title={`Things to do Near ${landmark.name}`}
                    description={fullList.destination_description || ''}
                    activeIds={settings.categories}
                    onClick={(id) => {
                        setLoading((prev) => ({
                            ...prev,
                            changeLoading: true,
                            initChangeLoading: true,
                        }));
                        setSettings((prev) => ({
                            ...prev,
                            categories: prev.categories.includes(id)
                                ? prev.categories.filter((p) => p !== id)
                                : [...prev.categories, id],
                        }));
                    }}
                />
            )}

            {!!fullList.total && (
                <>
                    {loading.changeLoading ? (
                        <Preloader />
                    ) : (
                        <ActivitiesList
                            title={title}
                            defaultList={fullList?.items?.slice(0, 4)}
                            listName={`${currentDestination.name}`}
                            listId={`${currentDestination.id}`}
                            availableAttributes={[]}
                            item_list_id={''}
                            item_list_name={''}
                            placeholders={{}}
                        />
                    )}
                </>
            )}

            {!!blogs.length && (
                <div style={isDesktop ? { marginBottom: '60px' } : { marginBottom: '20px' }}>
                    <CardsContainer
                        title={`${name} Articles & Travel Tips`}
                        linkName={`More ${name} Articles`}
                        linkHref={`/blog/${state.code}/${slug}`}
                        description="Keeping You Informed"
                    >
                        {blogs.map((blog) => {
                            return (
                                <BlogCard
                                    cardLink={`/blog/${blog.slug}/`}
                                    imageSrc={`${URL.image}/blog/${blog.image.id}/1000x460/${blog.image.slug}.jpg`}
                                    date={blog.updated_at}
                                    title={blog.title}
                                    location={blog.category.name}
                                    key={blog.slug}
                                />
                            );
                        })}
                    </CardsContainer>
                </div>
            )}

            <AnswersFAQ
                title={title}
                url={meta.canonical}
                image={heroImages[0]}
                id={`l${landmark.id}`}
            />
        </Layout>
    );
};
